import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ChevronLeft, Check, Loader2, ClipboardCheck } from "lucide-react";
import { SearchableSelect } from "@/components/ui/searchable-select";

export const Route = createFileRoute("/_authenticated/inspections/new")({
  component: NewInspectionPage,
});

interface InspectionTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string;
  isActive: boolean;
}

function NewInspectionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");
  const [projectId, setProjectId] = useState("");

  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ["inspection-templates"],
    queryFn: () =>
      apiGet<{ data: InspectionTemplate[] }>("/api/admin/inspection-templates"),
  });

  const { data: projectsData } = useQuery({
    queryKey: ["projects", "active"],
    queryFn: () =>
      apiGet<{ data: Array<{ id: string; name: string }> }>(
        "/api/projects?status=active"
      ),
  });

  const templates = (templatesData?.data || []).filter((t) => t.isActive);
  const projects = projectsData?.data || [];

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiPost<{ data: { id: string } }>("/api/inspections", body),
    onSuccess: (res) => {
      toast.success("Inspection started");
      queryClient.invalidateQueries({ queryKey: ["inspections"] });
      navigate({
        to: "/inspections/$inspectionId/swipe",
        params: { inspectionId: res.data.id },
      });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center gap-2 pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="p-1"
          onClick={() => navigate({ to: "/inspections" })}
        >
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold text-text-primary">
          New Inspection
        </h1>
      </div>

      {/* Template selection */}
      <div className="space-y-2">
        <Label className="text-base font-semibold">
          Select Template *
        </Label>

        {templatesLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <ClipboardCheck className="h-8 w-8 text-muted-foreground/40 mb-2" />
              <p className="text-sm text-muted-foreground">
                No inspection templates available.
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Ask an admin to create one.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {templates.map((template) => (
              <Card
                key={template.id}
                className={cn(
                  "cursor-pointer transition-all",
                  selectedTemplateId === template.id
                    ? "border-paris-red bg-paris-red/5"
                    : "hover:bg-muted/30"
                )}
                onClick={() => setSelectedTemplateId(template.id)}
              >
                <CardContent className="flex items-center justify-between py-3 px-4">
                  <div>
                    <p className="text-sm font-medium">{template.name}</p>
                    {template.description && (
                      <p className="text-xs text-muted-foreground">
                        {template.description}
                      </p>
                    )}
                  </div>
                  {selectedTemplateId === template.id && (
                    <Check className="h-5 w-5 text-paris-red-text" />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>Date *</Label>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-12"
        />
      </div>

      <div className="space-y-2">
        <Label>Project</Label>
        <SearchableSelect
          className="h-12"
          value={projectId}
          onChange={setProjectId}
          allLabel="None"
          allValue=""
          options={projects.map((p) => ({ value: p.id, label: p.name }))}
        />
      </div>

      <div className="space-y-2">
        <Label>Notes</Label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Any notes..."
          rows={2}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base"
        />
      </div>

      <Button
        className="w-full h-12 bg-paris-red hover:bg-paris-red-dark"
        disabled={!selectedTemplateId || !date || createMutation.isPending}
        onClick={() =>
          createMutation.mutate({
            templateId: selectedTemplateId,
            date,
            notes: notes || undefined,
            projectId: projectId || undefined,
          })
        }
      >
        {createMutation.isPending ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          "Start Inspection"
        )}
      </Button>
    </div>
  );
}
